import { AuthenticationService } from '../services/authentication.service';
import { Observable, BehaviorSubject, Subject, throwError, catchError, switchMap, filter, take, tap, finalize, map } from 'rxjs';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationModel } from '../services/api-services';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  tokenRefreshedSource = new Subject();
  tokenRefreshed$ = this.tokenRefreshedSource.asObservable();

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private readonly authenticationService: AuthenticationService,
    private readonly router: Router) {

  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const token = this.authenticationService.getToken();
    // Get current browser route
    const browserRoute = this.router.url || '';

    if (token) {
      request = this.addTokenAndRoute(request, token, browserRoute);
    } else {
      request = this.addRoute(request, browserRoute);
    }

    // Handle response
    return next.handle(request).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(request, next);
        }
        else {
          return throwError(() => error);
        }
      }));
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      // Cancel previous pending requests
      this.refreshTokenSubject.next(null);

      return this.authenticationService.refreshToken().pipe(
        switchMap((result: AuthenticationModel | null) => {
          this.refreshTokenSubject.next(result?.token);

          if (result?.token != null) {
            const browserRoute = window.location.pathname + window.location.search;
            return next.handle(this.addTokenAndRoute(request, result.token, browserRoute));
          }
          else {
            // Return error and go into catchError to redirect
            return throwError(() => new Error('No token received.'));
          }
        }),
        catchError(error => {
          // Navigate to login page if refresh failed
          this.router.navigate([this.authenticationService.getLoginUrl()]);

          // Cancel pending requests
          this.refreshTokenSubject.next(null);

          return throwError(() => error);
        }),
        finalize(() => this.isRefreshing = false));
    }
    else {
      // Let other requests wait on the refreshed token
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(jwt => {
          const browserRoute = window.location.pathname + window.location.search;
          return next.handle(this.addTokenAndRoute(request, jwt!, browserRoute));
        }));
    }
  }

  private addTokenAndRoute(request: HttpRequest<any>, token: string, browserRoute: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'X-Client-Route': browserRoute
      }
    });
  }

  private addRoute(request: HttpRequest<any>, browserRoute: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        'X-Client-Route': browserRoute
      }
    });
  }
}
